<template>
  <main class="explorer" :class="{ compact }">
    <ExplorerTopBar v-if="!showEdit" :is-login="isLogin" :display-name="displayName" @login="openLoginDialog" @logout="logout" @profile="openProfile" />

    <CommandHero
      v-if="showHomeRoute && !showEdit && !showProfile && !activeLevel"
      :archive-count="archiveTotal || archiveLevels.length"
      :mine-count="mineLevels.length"
      :favorite-count="favoriteLevels.length"
      :visit-summary="visitSummary"
      @random="randomLevel"
      @refresh="refreshHome"
      @open-mine="openMineFromSummary"
      @open-favorites="openFavoritesFromSummary"
      @feedback="feedbackDialogVisible = true"
      @visit-stats="openVisitStats"
    />

    <VisitStatsPanel
      v-if="!showEdit && !showProfile && showVisitStats"
      :visit-summary="visitSummary"
      @back="closeVisitStats"
      @refresh="loadVisitDashboard"
    />

    <LevelReader
      v-if="!showEdit && !showProfile && !showVisitStats && activeLevel"
      :active-level="activeLevel"
      :active-source="activeSource"
      :source-text="sourceText"
      :messages="messages"
      :is-owner="isOwner"
      :patrol-expire-days="patrolExpireDays"
      v-model:message-content="messageContent"
      @leave="leaveLevel"
      @occupy="occupyLevel"
      @favorite="toggleFavorite"
      @guard="guardLevel"
      @patrol="patrolActiveLevel"
      @send="sendMessage"
    />

    <LevelLibrary
      v-if="showHomeRoute && !showEdit && !showProfile && !activeLevel"
      v-model:tab="tab"
      v-model:search-field="searchField"
      v-model:keyword="keyword"
      v-model:status-filter="statusFilter"
      v-model:level-sort="levelSort"
      :tab-title="tabTitle"
      :levels="currentLevels"
      :patrol-expire-days="patrolExpireDays"
      :archive-loading="archiveLoading"
      :archive-loading-more="archiveLoadingMore"
      :archive-has-next="archiveHasNext"
      :archive-reached-end="archiveReachedEnd"
      :archive-total="archiveTotal"
      @load-archive="loadArchive"
      @load-archive-more="loadArchiveMore"
      @open-mine="openMine"
      @open-favorites="openFavorites"
      @open-level="openLevel"
      @open-edit="openEdit"
      @patrol-level="patrolLevel"
    />

    <ProfileCenter
      v-if="!showEdit && showProfile"
      :display-name="displayName"
      @close="closeProfile"
    />

    <EditorStudio
      v-if="showEdit"
      :edit-form="editForm"
      :edit-saving="editSaving"
      :uploading-field="uploadingField"
      :edit-preview-style="editPreviewStyle"
      :edit-cover-style="editCoverStyle"
      :edit-cover-url="editCoverUrl"
      :patrol-expire-days="patrolExpireDays"
      :draft-dialog-visible="draftDialogVisible"
      :draft-items="levelDrafts"
      :draft-loading="draftLoading"
      :draft-saving="draftSaving"
      :active-draft-id="activeDraftId"
      :active-draft-name="activeDraftName"
      :current-draft-update-at="currentDraftUpdateAt"
      @close="closeEdit"
      @save="saveLevel"
      @draft-save="saveLevelDraft"
      @draft-create="createLevelDraft"
      @draft-open="openDraftDirectory"
      @draft-search="loadLevelDrafts"
      @draft-select="selectLevelDraft"
      @draft-delete="deleteLevelDraft"
      @update:draft-dialog-visible="draftDialogVisible = $event"
      @patrol="patrolEditingLevel"
      @image-file="handleImageFile"
    />

    <AuthDialog
      v-model="showLogin"
      v-model:auth-mode="authMode"
      :login-form="loginForm"
      :login-rules="loginRules"
      :loading="loginLoading"
      @submit="submitAuth"
    />

    <FeedbackDialog v-model="feedbackDialogVisible" />
  </main>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import { ajax } from '@/api';
import { useAuthStore } from '@/store/auth';
import { userData } from '@/store/userData';
import ExplorerTopBar from './level-explorer/top-bar.vue';
import CommandHero from './level-explorer/command-hero.vue';
import LevelReader from './level-explorer/level-reader.vue';
import LevelLibrary from './level-explorer/level-library.vue';
import EditorStudio from './level-explorer/editor-studio.vue';
import AuthDialog from './level-explorer/auth-dialog.vue';
import FeedbackDialog from './level-explorer/feedback-dialog.vue';
import ProfileCenter from './level-explorer/profile-center.vue';
import VisitStatsPanel from './level-explorer/visit-stats-panel.vue';
import type { LevelDraftItem, LevelItem, LevelSort, LevelSource, LevelTab, SearchField } from './level-explorer/types';
import { useLevelExplorerAuth } from './level-explorer/auth';
import { useLevelExplorerCollections } from './level-explorer/collections';
import { createEmptyLevelDraft, createLevelDraftFromItem } from './level-explorer/draft';
import { useLevelExplorerEditMedia } from './level-explorer/edit-media';
import { useLevelExplorerRandomSession } from './level-explorer/random-session';
import {
  explorerVisitDashboardPath,
  useLevelExplorerScene
} from './level-explorer/scene';

defineProps<{ compact?: boolean }>();

type VisitSummary = {
  visible: boolean;
  loading: boolean;
  todayVisitors: number;
  todayVisits: number;
  weekVisits: number;
  monthVisitors: number;
  monthVisits: number;
};

const tab = ref<LevelTab>('archive');
const archiveLevels = ref<LevelItem[]>([]);
const mineLevels = ref<LevelItem[]>([]);
const favoriteLevels = ref<LevelItem[]>([]);
const activeLevel = ref<LevelItem | null>(null);
const activeSource = ref<LevelSource>('archive');
const messages = ref<any[]>([]);
const messageContent = ref('');
const searchField = ref<SearchField>('name');
const keyword = ref('');
const statusFilter = ref<number | ''>('');
const levelSort = ref<LevelSort>('asc');
const showProfile = ref(false);
const patrolExpireDays = ref(5);
const authStore = useAuthStore();
const useData = userData();
const route = useRoute();
const router = useRouter();
const showEdit = ref(false);
const feedbackDialogVisible = ref(false);
const editSaving = ref(false);
const draftDialogVisible = ref(false);
const draftLoading = ref(false);
const draftSaving = ref(false);
const levelDrafts = ref<LevelDraftItem[]>([]);
const activeDraftId = ref<number | null>(null);
const activeDraftName = ref('当前');
const currentLevelSnapshot = ref<Partial<LevelItem> | null>(null);
const visitSummary = reactive<VisitSummary>({
  visible: false,
  loading: false,
  todayVisitors: 0,
  todayVisits: 0,
  weekVisits: 0,
  monthVisitors: 0,
  monthVisits: 0
});
const editForm = reactive<Partial<LevelItem>>(createEmptyLevelDraft());
const { showLogin, loginLoading, authMode, loginForm, loginRules, openLoginDialog, requireLogin, submitAuth, logout } =
  useLevelExplorerAuth({
    authStore,
    userData: useData,
    mineLevels,
    favoriteLevels,
    showProfile,
    loadVisitDashboard: () => loadVisitDashboard()
  });
const { uploadingField, backgroundPreviewUrl, coverPreviewUrl, revokePreviewUrls, handleImageFile } =
  useLevelExplorerEditMedia({
    editForm,
    requireLogin
  });

const isLogin = computed(() => !!authStore.loginKey || !!sessionStorage.getItem('loginKey'));
const displayName = computed(() => useData.name || sessionStorage.getItem('name') || '探索者');
const sourceText = computed(() => {
  if (activeSource.value === 'random') return 'TRANSIT MODE';
  if (activeSource.value === 'mine') return 'MY LAYER';
  if (activeSource.value === 'favorite') return 'SAVED FILE';
  return 'ARCHIVE MODE';
});
const tabTitle = computed(() => {
  if (tab.value === 'mine') return '我的占领层级';
  if (tab.value === 'favorite') return '收藏索引';
  return '公开层级档案';
});

const currentLevels = computed(() => {
  if (tab.value === 'mine') return mineLevels.value;
  if (tab.value === 'favorite') return favoriteLevels.value;
  return archiveLevels.value;
});

const isOwner = computed(() => {
  const userId = Number(sessionStorage.getItem('userId') || 0);
  const name = sessionStorage.getItem('name') || '';
  return !!activeLevel.value && (activeLevel.value.ownerUserId === userId || (!!name && activeLevel.value.ownerName === name));
});

const editBackgroundUrl = computed(() => backgroundPreviewUrl.value || editForm.backgroundImg || editForm.img || coverPreviewUrl.value || '');
const editCoverUrl = computed(() => coverPreviewUrl.value || editForm.img || '');
const editPreviewStyle = computed(() => {
  return editBackgroundUrl.value ? { backgroundImage: `url(${editBackgroundUrl.value})` } : {};
});
const editCoverStyle = computed(() => {
  return editCoverUrl.value ? { backgroundImage: `url(${editCoverUrl.value})` } : {};
});
const currentDraftUpdateAt = computed(() => currentLevelSnapshot.value?.updateAt || '');
const {
  showHomeRoute,
  showVisitStats,
  showRandomLevel,
  goHome,
  leaveVisitStatsRoute,
  syncRouteView
} = useLevelExplorerScene(
  route,
  router,
  {
    showProfile,
    showEdit,
    activeLevel,
    activeSource,
    messages
  },
  {
    requireLogin: () => requireLogin(),
    loadVisitDashboard: () => loadVisitDashboard(),
    loadRandomLevel: () => loadRandomLevel(),
    loadLevelDetail: (level, onSuccess) => loadLevelDetail(level, onSuccess),
    openEditWithDetail: (level) => openEditWithDetail(level)
  }
);
const {
  archiveLoading,
  archiveLoadingMore,
  archiveHasNext,
  archiveReachedEnd,
  archiveTotal,
  loadArchive,
  loadArchiveMore,
  openLevel,
  loadLevelDetail,
  loadMineLevels,
  loadFavoriteLevels,
  openMine,
  openFavorites,
  loadPublicSettings,
  patrolLevel,
  patrolActiveLevel,
  patrolEditingLevel
} =
  useLevelExplorerCollections({
    tab,
    archiveLevels,
    mineLevels,
    favoriteLevels,
    activeLevel,
    searchField,
    keyword,
    statusFilter,
    levelSort,
    patrolExpireDays,
    editForm,
    requireLogin,
    leaveVisitStatsRoute,
    router
  });
const { loadRandomLevel, randomLevel, leaveLevel, occupyLevel, guardLevel, toggleFavorite, sendMessage } =
  useLevelExplorerRandomSession({
    activeLevel,
    activeSource,
    messages,
    messageContent,
    showProfile,
    showEdit,
    showRandomLevel,
    requireLogin,
    leaveVisitStatsRoute,
    goHome: () => goHome(),
    router,
    loadArchive: () => loadArchive()
  });

const numberValue = (value: unknown) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const resetVisitSummary = () => {
  visitSummary.visible = false;
  visitSummary.loading = false;
  visitSummary.todayVisitors = 0;
  visitSummary.todayVisits = 0;
  visitSummary.weekVisits = 0;
  visitSummary.monthVisitors = 0;
  visitSummary.monthVisits = 0;
};

const loadVisitDashboard = () => {
  visitSummary.loading = true;
  visitSummary.visible = true;
  ajax({
    url: 'visit/dashboard',
    method: 'POST',
    data: {},
    isLoading: false,
    success: (res: any) => {
      visitSummary.loading = false;
      if (res.code !== 0) {
        visitSummary.visible = false;
        ElMessage.warning(res.msg || '访客统计读取失败');
        return;
      }
      const summary = res.data?.summary || {};
      visitSummary.visible = true;
      visitSummary.todayVisitors = numberValue(summary.todayVisitors);
      visitSummary.todayVisits = numberValue(summary.todayVisits);
      visitSummary.weekVisits = numberValue(summary.weekVisits);
      visitSummary.monthVisitors = numberValue(summary.monthVisitors);
      visitSummary.monthVisits = numberValue(summary.monthVisits);
    },
    error: () => {
      resetVisitSummary();
    }
  });
};

const loadHomeCollections = () => {
  if (!isLogin.value) return;
  loadMineLevels(true);
  loadFavoriteLevels(true);
};

const refreshHome = () => {
  loadArchive();
  loadVisitDashboard();
  loadHomeCollections();
};

const scrollToLibrary = () => {
  nextTick(() => {
    document.querySelector('.explorer .workspace')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  });
};

const openMineFromSummary = () => {
  openMine();
  if (isLogin.value) scrollToLibrary();
};

const openFavoritesFromSummary = () => {
  openFavorites();
  if (isLogin.value) scrollToLibrary();
};

const openProfile = () => {
  if (!requireLogin()) return;
  activeLevel.value = null;
  leaveVisitStatsRoute();
  showProfile.value = true;
};

const closeProfile = () => {
  showProfile.value = false;
};

const openVisitStats = () => {
  activeLevel.value = null;
  showProfile.value = false;
  showEdit.value = false;
  router.push(explorerVisitDashboardPath);
  loadVisitDashboard();
};

const closeVisitStats = () => {
  goHome();
};

const openEdit = (level: LevelItem) => {
  if (!requireLogin()) return;
  router.push(`/level/${level.id}/edit`);
};

const openEditWithDetail = (level: LevelItem) => {
  revokePreviewUrls();
  leaveVisitStatsRoute();
  const currentDraft = createLevelDraftFromItem(level);
  Object.assign(editForm, currentDraft);
  currentLevelSnapshot.value = { ...currentDraft };
  activeDraftId.value = null;
  activeDraftName.value = '当前';
  levelDrafts.value = [];
  draftDialogVisible.value = false;
  showEdit.value = true;
  loadCurrentLevelForEdit(Number(level.id));
};

const closeEdit = () => {
  showEdit.value = false;
  draftDialogVisible.value = false;
  activeDraftId.value = null;
  activeDraftName.value = '当前';
  currentLevelSnapshot.value = null;
  levelDrafts.value = [];
  revokePreviewUrls();
  goHome();
  openMine();
};

const buildLevelEditPayload = () => {
  const payload = { ...editForm } as Record<string, any>;
  delete payload.levelNum;
  delete payload.ownerUserId;
  delete payload.ownerName;
  delete payload.occupiedAt;
  delete payload.guardedAt;
  delete payload.enterNum;
  delete payload.messageNum;
  delete payload.favoriteNum;
  delete payload.status;
  delete payload.auditStatus;
  delete payload.rejectReason;
  delete payload.randomToken;
  delete payload.createAt;
  delete payload.updateAt;
  return payload;
};

const saveLevel = async () => {
  if (!editForm.id || !requireLogin()) return;
  if (uploadingField.value) {
    ElMessage.warning('图片还在上传，请稍后保存');
    return;
  }
  try {
    await ElMessageBox.confirm(
      '提交审核后，需要管理员审核通过后，公开页面才会显示本次编辑的最新内容。审核期间，原有已发布内容仍保持不变。',
      '确认提交审核',
      {
        confirmButtonText: '确认提交',
        cancelButtonText: '继续编辑',
        type: 'warning',
        customClass: 'level-audit-confirm-box'
      }
    );
  } catch {
    return;
  }

  editSaving.value = true;
  const payload = buildLevelEditPayload();
  ajax({
    url: 'level/update',
    method: 'POST',
    data: payload,
    success: (res: any) => {
      editSaving.value = false;
      if (res.code === 0) {
        ElMessage.success('层级信息已提交，等待管理员审核');
        closeEdit();
      } else {
        ElMessage.error(res.msg);
      }
    },
    error: () => {
      editSaving.value = false;
      ElMessage.error('保存失败，请稍后重试');
    }
  });
};

const loadCurrentLevelForEdit = (levelId: number) => {
  if (!levelId) return;
  ajax({
    url: `level/draft/current/${levelId}`,
    method: 'GET',
    isLoading: false,
    success: (res: any) => {
      if (res.code !== 0 || !res.data) return;
      const currentDraft = createLevelDraftFromItem(res.data);
      currentLevelSnapshot.value = { ...currentDraft };
      if (showEdit.value && activeDraftId.value === null && Number(editForm.id) === levelId) {
        Object.assign(editForm, currentDraft);
      }
    }
  });
};

const openDraftDirectory = () => {
  if (!editForm.id || !requireLogin()) return;
  draftDialogVisible.value = true;
  loadLevelDrafts('');
};

const loadLevelDrafts = (keyword = '') => {
  if (!editForm.id || !requireLogin()) return;
  draftLoading.value = true;
  ajax({
    url: 'level/draft/list',
    method: 'POST',
    data: {
      levelId: editForm.id,
      keyword
    },
    isLoading: false,
    success: (res: any) => {
      draftLoading.value = false;
      if (res.code === 0) {
        levelDrafts.value = res.dataList || [];
      } else {
        ElMessage.error(res.msg || '暂存目录读取失败');
      }
    },
    error: () => {
      draftLoading.value = false;
      ElMessage.error('暂存目录读取失败，请稍后重试');
    }
  });
};

const applyLevelDraftToForm = (draft: Partial<LevelDraftItem>) => {
  const base = currentLevelSnapshot.value || createLevelDraftFromItem(editForm);
  const merged = {
    ...base,
    ...draft,
    id: base.id || editForm.id || draft.levelId || 0
  };
  Object.assign(editForm, createLevelDraftFromItem(merged));
};

const selectLevelDraft = (draft: Partial<LevelDraftItem> & { isDefault?: boolean }) => {
  if (draft.isDefault || !draft.id) {
    const currentDraft = currentLevelSnapshot.value || createLevelDraftFromItem(editForm);
    Object.assign(editForm, createLevelDraftFromItem(currentDraft));
    activeDraftId.value = null;
    activeDraftName.value = '当前';
  } else {
    applyLevelDraftToForm(draft);
    activeDraftId.value = Number(draft.id);
    activeDraftName.value = draft.directoryName || `暂存目录 ${draft.id}`;
  }
  draftDialogVisible.value = false;
};

const promptDraftName = async (initialName = '') => {
  const result = await ElMessageBox.prompt('给这份暂存资料起一个目录名称，方便下次继续编辑。', '暂存目录', {
    confirmButtonText: '保存',
    cancelButtonText: '取消',
    customClass: 'draft-name-prompt-box',
    inputValue: initialName,
    inputPlaceholder: '例如：公告改版 / 正文重写',
    inputValidator: (value) => {
      const text = String(value || '').trim();
      if (!text) return '请输入目录名称';
      if (text.length > 30) return '目录名称不能超过30个字';
      return true;
    }
  });
  return String(result.value || '').trim();
};

const persistLevelDraft = async (forceNew = false) => {
  if (!editForm.id || !requireLogin()) return;
  if (uploadingField.value) {
    ElMessage.warning('图片还在上传，请稍后暂存');
    return;
  }

  const shouldCreate = forceNew || activeDraftId.value === null;
  let directoryName = activeDraftName.value;
  if (shouldCreate) {
    try {
      directoryName = await promptDraftName(forceNew ? '' : activeDraftName.value === '当前' ? '' : activeDraftName.value);
    } catch {
      return;
    }
  }

  draftSaving.value = true;
  const editPayload = buildLevelEditPayload();
  const payload = {
    ...editPayload,
    id: shouldCreate ? null : activeDraftId.value,
    levelId: editForm.id,
    directoryName
  };
  ajax({
    url: 'level/draft/save',
    method: 'POST',
    data: payload,
    success: (res: any) => {
      draftSaving.value = false;
      if (res.code === 0) {
        const savedDraft = res.data as LevelDraftItem;
        if (savedDraft?.id) {
          activeDraftId.value = savedDraft.id;
          activeDraftName.value = savedDraft.directoryName || directoryName;
          const index = levelDrafts.value.findIndex((item) => item.id === savedDraft.id);
          if (index >= 0) levelDrafts.value[index] = savedDraft;
          else levelDrafts.value.unshift(savedDraft);
        }
        ElMessage.success(res.msg || '暂存成功');
      } else {
        ElMessage.error(res.msg || '暂存失败');
      }
    },
    error: () => {
      draftSaving.value = false;
      ElMessage.error('暂存失败，请稍后重试');
    }
  });
};

const saveLevelDraft = () => {
  persistLevelDraft(false);
};

const createLevelDraft = () => {
  persistLevelDraft(true);
};

const deleteLevelDraft = async (draft: LevelDraftItem) => {
  if (!draft?.id || !requireLogin()) return;
  try {
    await ElMessageBox.confirm(`确定删除「${draft.directoryName || '未命名目录'}」吗？`, '删除暂存目录', {
      confirmButtonText: '删除',
      cancelButtonText: '取消',
      type: 'warning'
    });
  } catch {
    return;
  }

  ajax({
    url: `level/draft/delete/${draft.id}`,
    method: 'POST',
    success: (res: any) => {
      if (res.code === 0) {
        levelDrafts.value = levelDrafts.value.filter((item) => item.id !== draft.id);
        if (activeDraftId.value === draft.id) {
          selectLevelDraft({ isDefault: true });
        }
        ElMessage.success('删除成功');
      } else {
        ElMessage.error(res.msg || '删除失败');
      }
    },
    error: () => {
      ElMessage.error('删除失败，请稍后重试');
    }
  });
};

onMounted(() => {
  loadPublicSettings();
  loadArchive();
  loadVisitDashboard();
  loadHomeCollections();
  syncRouteView();
});
watch(
  () => isLogin.value,
  (loggedIn) => {
    if (loggedIn) loadHomeCollections();
  }
);
watch(
  () => route.fullPath,
  () => syncRouteView()
);
onBeforeUnmount(() => {
  revokePreviewUrls();
});
</script>

<style lang="scss">
.explorer {
  --bg: var(--cyber-bg, #0b101e);
  --surface: rgba(11, 16, 30, 0.74);
  --surface-2: rgba(15, 23, 42, 0.86);
  --line: rgba(0, 240, 216, 0.22);
  --line-strong: rgba(0, 240, 216, 0.48);
  --text: var(--cyber-text, #e8edf7);
  --muted: var(--cyber-muted, #8992a9);
  --soft: rgba(137, 146, 169, 0.58);
  --accent: var(--cyber-cyan, #00f0d8);
  --accent-2: var(--cyber-purple, #9d5cff);
  --accent-ink: #06101d;
  --blue: var(--cyber-blue, #22b8ff);
  min-height: 100vh;
  color: var(--text);
  font-family: var(--cyber-font, "Inter", "PingFang SC", "Microsoft YaHei", Arial, sans-serif);
  background:
    linear-gradient(rgba(0, 240, 216, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(157, 92, 255, 0.032) 1px, transparent 1px),
    radial-gradient(circle at 18% 6%, rgba(34, 184, 255, 0.12), transparent 34%),
    radial-gradient(circle at 86% 8%, rgba(157, 92, 255, 0.1), transparent 30%),
    linear-gradient(180deg, #0f172a, #0b101e 48%, #070b14);
  background-size: 72px 72px, 72px 72px, auto, auto, auto;
  padding: 22px;
  overflow-x: hidden;
}

.topbar,
.command,
.workspace,
.level-brief,
.action-panel {
  border: 1px solid var(--line);
  background:
    linear-gradient(180deg, rgba(0, 240, 216, 0.05), rgba(157, 92, 255, 0.024)),
    var(--surface);
  backdrop-filter: blur(26px) saturate(125%);
  box-shadow:
    0 24px 80px rgba(0, 0, 0, 0.38),
    0 0 26px rgba(0, 240, 216, 0.055),
    inset 0 1px 0 rgba(0, 240, 216, 0.1);
  border-radius: 8px;
}

.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  max-width: 1320px;
  margin: 0 auto 18px;
  padding: 12px 14px 12px 12px;
}

.identity,
.top-actions,
.command-actions,
.panel-actions,
.filters,
.tabs {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.identity {
  gap: 12px;
}

.identity strong {
  display: block;
  letter-spacing: 0;
  font-size: 15px;
}

.identity span,
.eyebrow,
.operator,
.brief-heading small,
.card-body small {
  color: var(--muted);
  font-size: 12px;
  letter-spacing: 0;
  text-transform: uppercase;
}

.sigil {
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  border: 1px solid rgba(0, 240, 216, 0.44);
  background: rgba(0, 240, 216, 0.09);
  color: var(--accent);
  font-weight: 800;
  border-radius: 8px;
  box-shadow: 0 0 22px rgba(0, 240, 216, 0.15);
}

.operator {
  padding: 9px 12px;
  border: 1px solid var(--line);
  background: rgba(0, 240, 216, 0.055);
  border-radius: 8px;
}


.top-actions .login-entry-btn {
  --el-button-bg-color: rgba(157, 92, 255, 0.12);
  --el-button-border-color: rgba(157, 92, 255, 0.46);
  --el-button-text-color: #c7a8ff;
  --el-button-hover-bg-color: rgba(157, 92, 255, 0.2);
  --el-button-hover-border-color: rgba(157, 92, 255, 0.72);
  --el-button-hover-text-color: #eadcff;
  box-shadow: 0 0 22px rgba(157, 92, 255, 0.1);
}

.command-actions .refresh-archive-btn {
  --el-button-bg-color: rgba(34, 184, 255, 0.1);
  --el-button-border-color: rgba(34, 184, 255, 0.42);
  --el-button-text-color: var(--blue);
  --el-button-hover-bg-color: rgba(34, 184, 255, 0.17);
  --el-button-hover-border-color: rgba(34, 184, 255, 0.7);
  --el-button-hover-text-color: #bfeaff;
  box-shadow: 0 0 22px rgba(34, 184, 255, 0.09);
}

.command-actions .feedback-entry-btn {
  --el-button-bg-color: rgba(245, 197, 66, 0.1);
  --el-button-border-color: rgba(245, 197, 66, 0.42);
  --el-button-text-color: #f7d879;
  --el-button-hover-bg-color: rgba(245, 197, 66, 0.17);
  --el-button-hover-border-color: rgba(245, 197, 66, 0.72);
  --el-button-hover-text-color: #fff1b8;
  box-shadow: 0 0 22px rgba(245, 197, 66, 0.09);
}

.command {
  max-width: 1320px;
  min-height: 420px;
  margin: 0 auto 18px;
  padding: 42px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 34px;
  align-items: end;
  position: relative;
  overflow: hidden;
}

.command::after {
  content: "";
  position: absolute;
  width: 42%;
  min-width: 300px;
  height: 100%;
  right: 0;
  top: 0;
  border-left: 1px solid rgba(0, 240, 216, 0.12);
  background:
    repeating-linear-gradient(90deg, rgba(0, 240, 216, 0.08) 0 1px, transparent 1px 18px),
    repeating-linear-gradient(0deg, rgba(157, 92, 255, 0.055) 0 1px, transparent 1px 24px),
    linear-gradient(90deg, transparent, rgba(0, 240, 216, 0.055));
  opacity: 0.82;
  pointer-events: none;
}

.command-copy {
  position: relative;
  z-index: 1;
  max-width: 780px;
}

.eyebrow {
  color: var(--blue);
  font-weight: 650;
}

h1,
h2,
h3,
h4,
p {
  margin: 0;
}

h1 {
  margin-top: 14px;
  font-size: clamp(42px, 6vw, 80px);
  line-height: 0.96;
  letter-spacing: 0;
  max-width: 820px;
}

.command-copy p {
  max-width: 620px;
  margin: 22px 0 28px;
  color: var(--muted);
  font-size: 16px;
  line-height: 1.72;
}

.command-guide-link {
  display: inline-flex;
  align-items: center;
  margin: -10px 0 26px;
  color: var(--accent);
  font-weight: 700;
  line-height: 1.7;
  text-decoration: underline;
  text-underline-offset: 5px;
  text-decoration-color: rgba(0, 240, 216, 0.45);
  transition: color 0.25s ease, text-decoration-color 0.25s ease, text-shadow 0.25s ease;
}

.command-guide-link:hover {
  color: #cffff8;
  text-decoration-color: rgba(0, 240, 216, 0.86);
  text-shadow: 0 0 14px rgba(0, 240, 216, 0.24);
}

.status-board {
  position: relative;
  z-index: 1;
  border: 1px solid var(--line);
  background: rgba(5, 10, 22, 0.46);
  padding: 18px;
  border-radius: 8px;
}

.board-row {
  display: flex;
  justify-content: space-between;
  padding: 14px 0;
  border-bottom: 1px solid rgba(0, 240, 216, 0.12);
}

.board-row-action {
  width: 100%;
  color: inherit;
  font: inherit;
  text-align: left;
  background: transparent;
  border: 0;
  border-bottom: 1px solid rgba(0, 240, 216, 0.12);
  cursor: pointer;
  transition: background-color 0.25s ease, padding 0.25s ease;
}

.board-row-action:hover {
  padding-inline: 10px;
  background: rgba(0, 240, 216, 0.06);
}

.board-row-action:focus-visible {
  outline: 1px solid rgba(0, 240, 216, 0.7);
  outline-offset: 4px;
}

.board-row span,
.board-note {
  color: var(--muted);
}

.board-row strong {
  color: var(--accent);
  font-size: 24px;
  letter-spacing: 0;
  text-shadow: 0 0 18px rgba(0, 240, 216, 0.22);
}

.visit-board-row strong {
  color: var(--blue);
}

.board-note {
  margin-top: 14px;
  line-height: 1.6;
  font-size: 12px;
}

.level-reader {
  position: relative;
  min-height: 76vh;
  margin: 0 -22px 18px;
  background-position: center;
  background-size: cover;
}

.reader-shade {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(90deg, rgba(5, 9, 20, 0.96), rgba(11, 16, 30, 0.68)),
    linear-gradient(180deg, rgba(11, 16, 30, 0.25), rgba(5, 9, 20, 0.94));
}

.reader-layout {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 390px;
  gap: 20px;
  max-width: 1320px;
  min-height: 76vh;
  margin: 0 auto;
  padding: 38px 22px;
  align-items: end;
}

.reader-layout.readonly-layout {
  grid-template-columns: minmax(0, 1fr);
  max-width: 1180px;
  align-items: stretch;
}

.readonly-layout .level-brief {
  max-width: none;
}

.readonly-banner {
  padding: 22px 24px;
  border: 1px solid rgba(0, 240, 216, 0.24);
  background:
    linear-gradient(135deg, rgba(0, 240, 216, 0.075), rgba(157, 92, 255, 0.035)),
    rgba(5, 10, 22, 0.68);
  border-radius: 8px;
  box-shadow: 0 18px 55px rgba(0, 0, 0, 0.34), 0 0 22px rgba(0, 240, 216, 0.08);
  backdrop-filter: blur(20px) saturate(125%);
}

.readonly-banner .section-title {
  margin: 0 0 8px;
}

.readonly-banner p {
  max-width: 820px;
  color: var(--muted);
  line-height: 1.8;
}

.level-brief,
.action-panel {
  padding: 24px;
}

.level-brief {
  max-width: 820px;
}

.text-button {
  color: var(--accent);
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
  margin-bottom: 24px;
  transition: color 0.28s ease, text-shadow 0.28s ease;
}

.text-button:hover {
  color: #cffff8;
  text-shadow: 0 0 12px rgba(0, 240, 216, 0.24);
}

.reader-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  margin-bottom: 26px;
  padding: 0 16px;
  color: var(--accent);
  border: 1px solid rgba(0, 240, 216, 0.42);
  background: rgba(0, 240, 216, 0.09);
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 0 22px rgba(0, 240, 216, 0.11);
  transition: border-color 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;
}

.reader-back::before {
  content: "<";
  font-weight: 700;
}

.reader-back:hover {
  border-color: rgba(0, 240, 216, 0.68);
  background: rgba(0, 240, 216, 0.14);
  box-shadow: 0 0 28px rgba(0, 240, 216, 0.16);
  transform: translateY(-1px);
}

.brief-heading {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.brief-heading span {
  color: var(--accent);
  border: 1px solid rgba(0, 240, 216, 0.36);
  background: rgba(0, 240, 216, 0.08);
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 700;
  border-radius: 8px;
}

.level-brief h2 {
  margin: 16px 0;
  font-size: clamp(38px, 5vw, 64px);
  line-height: 0.98;
  letter-spacing: 0;
}

.level-brief p {
  color: var(--muted);
  line-height: 1.8;
}

.reader-cover {
  width: min(560px, 100%);
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr);
  gap: 14px;
  align-items: center;
  margin-top: 22px;
  padding: 12px;
  border: 1px solid rgba(0, 240, 216, 0.18);
  background: rgba(5, 10, 22, 0.48);
  border-radius: 8px;
  backdrop-filter: blur(16px);
}

.reader-cover-media {
  height: 116px;
  overflow: hidden;
  border: 1px solid rgba(0, 240, 216, 0.2);
  border-radius: 8px;
  background: #0b101e;
}

.reader-cover-media img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.reader-cover strong {
  display: block;
  margin-top: 8px;
  color: var(--text);
  line-height: 1.35;
}

.metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(118px, 1fr));
  gap: 10px;
  margin: 24px 0;
}

.metrics div {
  border: 1px solid rgba(0, 240, 216, 0.16);
  background: rgba(0, 240, 216, 0.045);
  padding: 13px;
  border-radius: 8px;
}

.metrics span {
  display: block;
  color: var(--muted);
  font-size: 12px;
}

.metrics strong {
  display: block;
  margin-top: 7px;
  color: var(--accent);
  letter-spacing: 0;
}

.metric-owner {
  word-break: break-word;
}

.metric-owner.vacant {
  color: #94a3b8;
}

.notice,
.level-html {
  border-left: 2px solid var(--blue);
  background: rgba(34, 184, 255, 0.055);
  padding: 14px 16px;
  border-radius: 0 8px 8px 0;
}

.level-html {
  margin-top: 16px;
  overflow: visible;
  line-height: 1.75;
}

.level-html ul,
.level-html ol {
  margin: 10px 0 10px 28px !important;
  padding-left: 24px !important;
  list-style-position: outside !important;
}

.level-html li {
  margin: 4px 0 !important;
  padding-left: 4px !important;
}

.level-html img {
  display: inline-block;
  max-width: 100%;
  height: auto;
  vertical-align: middle;
}

.level-html .w-e-image-container {
  display: inline-block;
  max-width: 100%;
}

.level-html .w-e-image-container img {
  width: 100%;
  height: 100%;
}

.level-html table {
  max-width: 100%;
  margin: 14px 0;
  border-collapse: collapse !important;
  border: 1px solid rgba(0, 240, 216, 0.34) !important;
  color: var(--text);
  background: rgba(5, 10, 22, 0.36);
}

.level-html th,
.level-html td {
  min-width: 44px;
  min-height: 32px;
  padding: 8px 10px;
  border: 1px solid rgba(0, 240, 216, 0.34) !important;
  line-height: 1.55;
  text-align: left;
  vertical-align: top;
}

.level-html th {
  color: #dffefa;
  font-weight: 800;
  background: rgba(0, 240, 216, 0.12);
}

.level-html td:empty::before,
.level-html th:empty::before {
  content: "\00a0";
}

.patrol-strip,
.audit-tip,
.editor-help {
  margin: 16px 0;
  padding: 14px 16px;
  border: 1px solid rgba(0, 240, 216, 0.18);
  border-left: 2px solid var(--accent);
  color: var(--text);
  background: rgba(0, 240, 216, 0.06);
  border-radius: 0 8px 8px 0;
}

.patrol-strip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.patrol-strip span,
.editor-help {
  color: var(--muted);
  line-height: 1.7;
}

.audit-tip {
  border-left-color: #f5c542;
  background: rgba(245, 197, 66, 0.08);
}

.audit-tip.reject {
  border-left-color: #ff6b7a;
  background: rgba(255, 107, 122, 0.08);
}

.section-title {
  color: var(--text);
}

.panel-actions {
  align-items: stretch;
}

.panel-actions .el-button {
  flex: 1;
  margin-left: 0;
  min-height: 42px;
  --el-button-bg-color: rgba(8, 13, 26, 0.72);
  --el-button-border-color: rgba(0, 240, 216, 0.3);
  --el-button-text-color: var(--muted);
  --el-button-hover-bg-color: rgba(0, 240, 216, 0.12);
  --el-button-hover-border-color: rgba(0, 240, 216, 0.58);
  --el-button-hover-text-color: var(--accent);
  box-shadow: 0 0 18px rgba(0, 240, 216, 0.07);
}

.panel-actions .favorite-action {
  --el-button-bg-color: rgba(157, 92, 255, 0.12);
  --el-button-border-color: rgba(157, 92, 255, 0.42);
  --el-button-text-color: #d4bdff;
  --el-button-hover-bg-color: rgba(157, 92, 255, 0.2);
  --el-button-hover-border-color: rgba(157, 92, 255, 0.72);
  --el-button-hover-text-color: #f0e8ff;
  box-shadow: 0 0 18px rgba(157, 92, 255, 0.1);
}

.panel-actions .guard-action {
  --el-button-bg-color: rgba(34, 184, 255, 0.1);
  --el-button-border-color: rgba(34, 184, 255, 0.4);
  --el-button-text-color: #a9e4ff;
  --el-button-hover-bg-color: rgba(34, 184, 255, 0.17);
  --el-button-hover-border-color: rgba(34, 184, 255, 0.7);
  --el-button-hover-text-color: #d9f5ff;
  box-shadow: 0 0 18px rgba(34, 184, 255, 0.09);
}

.section-title {
  margin: 22px 0 12px;
  font-weight: 700;
}

.message-list {
  max-height: 280px;
  overflow: auto;
  border-top: 1px solid rgba(0, 240, 216, 0.12);
  border-bottom: 1px solid rgba(0, 240, 216, 0.12);
}

.message-item {
  padding: 12px 0;
  border-bottom: 1px solid rgba(0, 240, 216, 0.09);
}

.message-item p {
  margin-top: 5px;
  color: var(--muted);
}

.message-input {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  margin-top: 12px;
}

.workspace {
  max-width: 1320px;
  margin: 0 auto;
  padding: 24px;
}

.workspace-head {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: end;
  margin-bottom: 18px;
}

.workspace-head h3 {
  margin-top: 6px;
  font-size: 30px;
  letter-spacing: 0;
}

.tabs button,
.edit-level-btn {
  border: 1px solid var(--line);
  background: rgba(8, 13, 26, 0.72);
  color: var(--muted);
  padding: 10px 14px;
  cursor: pointer;
  transition: border-color 0.3s ease, color 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease;
  border-radius: 8px;
}

.tabs button.active,
.tabs button:hover,
.edit-level-btn:hover {
  color: var(--accent);
  background: rgba(0, 240, 216, 0.1);
  border-color: rgba(0, 240, 216, 0.55);
  box-shadow: 0 0 20px rgba(0, 240, 216, 0.12);
}

.filters {
  margin-bottom: 18px;
}

.filters .search-field-select {
  width: 116px;
}

.filters .status-filter-select {
  width: 104px;
}

.filters .sort-select {
  width: 158px;
}

.filters .el-input {
  width: 320px;
}

.filters .el-select .el-input {
  width: 100%;
}

.level-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(282px, 1fr));
  gap: 16px;
}

.archive-loading,
.archive-load-state {
  grid-column: 1 / -1;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--muted);
  font-size: 13px;
}

.archive-load-state.is-idle {
  min-height: 1px;
}

.level-card {
  border: 1px solid var(--line);
  background: rgba(11, 16, 30, 0.66);
  cursor: pointer;
  overflow: hidden;
  transition: transform 0.3s ease, border-color 0.3s ease, background 0.3s ease, box-shadow 0.3s ease;
  border-radius: 8px;
}

.level-card:hover {
  transform: translateY(-3px);
  border-color: var(--line-strong);
  background: rgba(15, 23, 42, 0.76);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.36), 0 0 24px rgba(0, 240, 216, 0.1);
}

.cover {
  position: relative;
  height: 184px;
  background:
    linear-gradient(rgba(0, 240, 216, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(157, 92, 255, 0.04) 1px, transparent 1px),
    #0b101e;
  background-size: 26px 26px;
}

.cover img,
.empty-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.empty-cover {
  display: grid;
  place-items: center;
  color: var(--accent);
  font-family: "Courier New", monospace;
  text-shadow: 0 0 18px rgba(0, 240, 216, 0.22);
}

.cover::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(5, 9, 20, 0.78), transparent 58%);
}

.cover .status-badge,
.cover .audit-badge {
  position: absolute;
  z-index: 1;
  top: 10px;
  color: var(--accent);
  border: 1px solid rgba(0, 240, 216, 0.36);
  background: rgba(5, 9, 20, 0.72);
  padding: 5px 8px;
  font-size: 12px;
  border-radius: 8px;
}

.cover .status-badge {
  right: 10px;
}

.cover .audit-badge {
  left: 10px;
  color: #fde68a;
  border-color: rgba(250, 204, 21, 0.46);
  background: rgba(113, 63, 18, 0.58);
}

.card-body {
  padding: 16px;
}

.card-body h4 {
  margin: 8px 0;
  font-size: 19px;
  letter-spacing: 0;
}

.card-body p {
  min-height: 46px;
  color: var(--muted);
  line-height: 1.55;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.card-owner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 240, 216, 0.09);
}

.card-owner span {
  color: var(--muted);
  font-size: 12px;
}

.card-owner strong {
  color: var(--accent);
  font-size: 13px;
  text-align: right;
  letter-spacing: 0;
  word-break: break-word;
}

.card-owner.vacant strong {
  color: #94a3b8;
}

.card-stats {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 16px;
  color: var(--muted);
  font-size: 12px;
}

.patrol-note {
  margin-top: 12px;
  color: var(--muted);
  font-size: 12px;
  display: grid;
  gap: 6px;
  padding: 9px 10px;
  border: 1px solid rgba(0, 240, 216, 0.14);
  background: rgba(0, 240, 216, 0.045);
  border-radius: 8px;
}

.patrol-note .return-note {
  color: var(--accent);
}

.patrol-note .return-note.is-soon {
  color: #fde68a;
}

.patrol-note .return-note.is-expired {
  color: #fb7185;
}

.edit-level-btn {
  width: 100%;
  margin-top: 12px;
}

.patrol-level-btn {
  color: var(--blue);
  border-color: rgba(34, 184, 255, 0.4);
  background: rgba(34, 184, 255, 0.08);
}

.explorer .el-button {
  border-radius: 8px;
}

.explorer .el-button--primary {
  --el-button-bg-color: rgba(0, 240, 216, 0.1);
  --el-button-border-color: rgba(0, 240, 216, 0.58);
  --el-button-text-color: var(--accent);
  --el-button-hover-bg-color: rgba(0, 240, 216, 0.16);
  --el-button-hover-border-color: rgba(34, 184, 255, 0.72);
  --el-button-hover-text-color: #cffff8;
}

.level-audit-confirm-box.el-message-box {
  --el-messagebox-title-color: var(--cyber-text, #e8edf7);
  --el-messagebox-content-color: var(--cyber-muted, #8992a9);
  width: min(92vw, 460px);
  border: 1px solid rgba(0, 240, 216, 0.34) !important;
  border-radius: 10px;
  background:
    linear-gradient(145deg, rgba(0, 240, 216, 0.08), rgba(157, 92, 255, 0.045)),
    rgba(7, 12, 24, 0.98) !important;
  box-shadow: 0 28px 110px rgba(0, 0, 0, 0.64), 0 0 42px rgba(0, 240, 216, 0.12) !important;
}

.level-audit-confirm-box .el-message-box__header {
  padding: 22px 24px 8px;
}

.level-audit-confirm-box .el-message-box__title {
  color: var(--cyber-text, #e8edf7) !important;
  font-weight: 800;
}

.level-audit-confirm-box .el-message-box__content {
  padding: 12px 24px 18px;
  color: var(--cyber-muted, #8992a9) !important;
  line-height: 1.8;
}

.level-audit-confirm-box .el-message-box__status {
  color: #f5c542 !important;
}

.level-audit-confirm-box .el-message-box__btns {
  padding: 0 24px 24px;
}

.level-audit-confirm-box .el-button {
  min-height: 38px;
  border-radius: 8px;
  font-weight: 700;
}

.level-audit-confirm-box .el-button:not(.el-button--primary) {
  color: var(--cyber-muted, #8992a9) !important;
  border-color: rgba(0, 240, 216, 0.28) !important;
  background: rgba(8, 13, 26, 0.82) !important;
}

.level-audit-confirm-box .el-button:not(.el-button--primary):hover {
  color: var(--cyber-cyan, #00f0d8) !important;
  border-color: rgba(0, 240, 216, 0.58) !important;
  background: rgba(0, 240, 216, 0.1) !important;
}

.level-audit-confirm-box .el-button--primary {
  color: #03101a !important;
  border-color: rgba(0, 240, 216, 0.78) !important;
  background: linear-gradient(135deg, rgba(0, 240, 216, 0.96), rgba(34, 184, 255, 0.9)) !important;
  box-shadow: 0 0 26px rgba(0, 240, 216, 0.2) !important;
}

.level-audit-confirm-box .el-message-box__headerbtn .el-message-box__close {
  color: var(--cyber-cyan, #00f0d8) !important;
}

.draft-name-prompt-box.el-message-box {
  --el-messagebox-title-color: var(--cyber-text, #e8edf7);
  --el-messagebox-content-color: var(--cyber-muted, #8992a9);
  width: min(92vw, 460px);
  border: 1px solid rgba(55, 65, 81, 0.86) !important;
  border-radius: 10px;
  background:
    linear-gradient(145deg, rgba(0, 240, 216, 0.055), rgba(157, 92, 255, 0.035)),
    #080d1a !important;
  box-shadow: 0 28px 110px rgba(0, 0, 0, 0.64), 0 0 42px rgba(0, 240, 216, 0.1) !important;
}

.draft-name-prompt-box .el-message-box__header {
  padding: 22px 24px 8px;
}

.draft-name-prompt-box .el-message-box__title {
  color: var(--cyber-text, #e8edf7) !important;
  font-weight: 800;
}

.draft-name-prompt-box .el-message-box__content {
  padding: 12px 24px 18px;
  color: var(--cyber-muted, #8992a9) !important;
  line-height: 1.8;
}

.draft-name-prompt-box .el-message-box__message,
.draft-name-prompt-box .el-message-box__message p {
  color: var(--cyber-muted, #8992a9) !important;
}

.draft-name-prompt-box .el-message-box__input {
  padding-top: 12px;
}

.draft-name-prompt-box .el-input__wrapper {
  min-height: 40px;
  border-radius: 8px;
  background: rgba(8, 13, 26, 0.92) !important;
  box-shadow: 0 0 0 1px rgba(0, 240, 216, 0.5) inset, 0 0 18px rgba(0, 240, 216, 0.08) !important;
}

.draft-name-prompt-box .el-input__inner {
  color: var(--cyber-text, #e8edf7) !important;
  background: transparent !important;
  caret-color: var(--cyber-cyan, #00f0d8);
}

.draft-name-prompt-box .el-input__inner::placeholder {
  color: rgba(137, 146, 169, 0.72) !important;
}

.draft-name-prompt-box .el-message-box__btns {
  padding: 0 24px 24px;
}

.draft-name-prompt-box .el-button {
  min-height: 38px;
  border-radius: 8px;
  font-weight: 700;
}

.draft-name-prompt-box .el-button:not(.el-button--primary) {
  color: var(--cyber-muted, #8992a9) !important;
  border-color: rgba(55, 65, 81, 0.86) !important;
  background: rgba(8, 13, 26, 0.82) !important;
}

.draft-name-prompt-box .el-button:not(.el-button--primary):hover {
  color: var(--cyber-cyan, #00f0d8) !important;
  border-color: rgba(0, 240, 216, 0.58) !important;
  background: rgba(0, 240, 216, 0.1) !important;
}

.draft-name-prompt-box .el-button--primary {
  color: #03101a !important;
  border-color: rgba(0, 240, 216, 0.78) !important;
  background: linear-gradient(135deg, rgba(0, 240, 216, 0.96), rgba(34, 184, 255, 0.9)) !important;
  box-shadow: 0 0 26px rgba(0, 240, 216, 0.2) !important;
}

.draft-name-prompt-box .el-message-box__headerbtn .el-message-box__close {
  color: var(--cyber-muted, #8992a9) !important;
}

.draft-name-prompt-box .el-message-box__headerbtn:hover .el-message-box__close {
  color: var(--cyber-cyan, #00f0d8) !important;
}

.compact {
  padding: 14px;
}

.compact .command,
.compact .reader-layout,
.compact .workspace-head {
  grid-template-columns: 1fr;
}

@media (max-width: 980px) {
  .command,
  .reader-layout {
    grid-template-columns: 1fr;
  }

  .workspace-head,
  .topbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .reader-cover {
    grid-template-columns: 1fr;
  }

}
</style>
